/**
 * Datos técnicos del navegador que se guardan con un consentimiento como evidencia.
 * Ninguno identifica el hardware por sí solo; juntos permiten reconocer el mismo navegador.
 * Nunca falla: lo que el navegador no exponga queda en null.
 */
export async function collectDeviceEvidence(): Promise<Record<string, unknown>> {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const uaData = (nav as Navigator & { userAgentData?: { brands?: { brand: string; version: string }[]; mobile?: boolean; platform?: string } } | null)
    ?.userAgentData;
  const intl = safe(() => Intl.DateTimeFormat().resolvedOptions());
  return {
    collectedAt: new Date().toISOString(),
    timeZone: intl?.timeZone ?? null,
    timezoneOffset: new Date().getTimezoneOffset(),
    locale: intl?.locale ?? null,
    languages: nav?.languages ? [...nav.languages] : nav?.language ? [nav.language] : null,
    platform: nav?.platform ?? null,
    userAgentData: uaData
      ? { brands: (uaData.brands ?? []).map((b) => `${b.brand} ${b.version}`), mobile: uaData.mobile ?? null, platform: uaData.platform ?? null }
      : null,
    screen: typeof screen !== 'undefined'
      ? {
          width: screen.width,
          height: screen.height,
          availWidth: screen.availWidth,
          availHeight: screen.availHeight,
          colorDepth: screen.colorDepth,
          pixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : null
        }
      : null,
    viewport: typeof window !== 'undefined' ? { width: window.innerWidth, height: window.innerHeight } : null,
    hardwareConcurrency: nav?.hardwareConcurrency ?? null,
    deviceMemory: (nav as Navigator & { deviceMemory?: number } | null)?.deviceMemory ?? null,
    maxTouchPoints: nav?.maxTouchPoints ?? null,
    cookieEnabled: nav?.cookieEnabled ?? null,
    doNotTrack: nav?.doNotTrack ?? null,
    webgl: safe(webglInfo),
    canvasHash: await safeAsync(canvasHash)
  };
}

function webglInfo(): { vendor: string | null; renderer: string | null } | null {
  const gl = document.createElement('canvas').getContext('webgl') as WebGLRenderingContext | null;
  if (!gl) return null;
  const debug = gl.getExtension('WEBGL_debug_renderer_info');
  return {
    vendor: debug ? String(gl.getParameter(debug.UNMASKED_VENDOR_WEBGL)) : String(gl.getParameter(gl.VENDOR)),
    renderer: debug ? String(gl.getParameter(debug.UNMASKED_RENDERER_WEBGL)) : String(gl.getParameter(gl.RENDERER))
  };
}

/** El mismo dibujo se rasteriza distinto según GPU, controlador y fuentes del equipo. */
async function canvasHash(): Promise<string | null> {
  const canvas = document.createElement('canvas');
  canvas.width = 240;
  canvas.height = 60;
  const ctx = canvas.getContext('2d');
  if (!ctx || !crypto?.subtle) return null;
  ctx.textBaseline = 'top';
  ctx.font = "16px 'Arial'";
  ctx.fillStyle = '#f60';
  ctx.fillRect(100, 1, 62, 20);
  ctx.fillStyle = '#069';
  ctx.fillText('Pento mandato ✓ 1234', 2, 15);
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
  ctx.fillText('Pento mandato ✓ 1234', 4, 17);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canvas.toDataURL()));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

function safe<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}

async function safeAsync<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch {
    return null;
  }
}
